/* eslint-disable react-refresh/only-export-components */
import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

// Chip variants usando CVA con tokens semánticos
const chipVariants = cva(
  // Base styles
  'inline-flex items-center justify-center rounded-full font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-[var(--primary)] focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60',
  {
    variants: {
      variant: {
        default: 'border border-[var(--border-default)] bg-[var(--surface-0)] text-[var(--text-secondary)] hover:bg-[var(--surface-hover)] hover:border-[var(--border-strong)]',
        selected: 'border border-[var(--primary)] bg-[var(--primary-light)] text-[var(--primary)] hover:bg-[var(--primary-hover)] hover:bg-opacity-10 active:bg-[var(--primary-pressed)] active:bg-opacity-10',
        outline: 'border-2 border-[var(--primary)] bg-[var(--surface-0)] text-[var(--primary)] hover:bg-[var(--primary-light)]',
        success: 'border border-[var(--success)] bg-[var(--success-bg)] text-[var(--success)] hover:bg-[var(--success-hover)] hover:bg-opacity-10',
        warning: 'border border-[var(--warning)] bg-[var(--warning-bg)] text-[var(--warning)] hover:bg-[var(--warning-hover)] hover:bg-opacity-10',
        error: 'border border-[var(--error)] bg-[var(--error-bg)] text-[var(--error)] hover:bg-[var(--error-hover)] hover:bg-opacity-10',
        info: 'border border-[var(--info)] bg-[var(--info-bg)] text-[var(--info)] hover:bg-[var(--info-hover)] hover:bg-opacity-10',
      },
      size: {
        sm: 'h-[30px] px-3 gap-1.5 text-xs',
        md: 'h-[38px] px-4 gap-2 text-sm',
        lg: 'h-[46px] px-5 gap-2.5 text-base',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'md',
    },
  }
);

export interface ChipProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof chipVariants> {
  /** Icono a mostrar al inicio del chip */
  icon?: React.ReactNode;
  /** Icono o elemento a mostrar al final del chip (ej: botón de cerrar) */
  endIcon?: React.ReactNode;
  /** Badge/contador a mostrar */
  badge?: string | number;
  /** Callback cuando se hace clic en el endIcon */
  onEndIconClick?: (e: React.MouseEvent) => void;
  /** Si el chip está seleccionado (controla el variant automáticamente) */
  selected?: boolean;
}

const Chip = React.forwardRef<HTMLButtonElement, ChipProps>(
  (
    {
      className,
      variant,
      size = 'md',
      icon,
      endIcon,
      badge,
      children,
      selected,
      onEndIconClick,
      type = 'button',
      ...props
    },
    ref
  ) => {
    // Si selected es true, sobrescribe el variant a 'selected'
    const effectiveVariant = selected ? 'selected' : variant;

    // Tamaños de iconos según el size del chip
    const iconSize = {
      sm: 'w-3.5 h-3.5',
      md: 'w-4 h-4',
      lg: 'w-5 h-5',
    }[size as string] || 'w-4 h-4';

    // Estilos del badge según el variant usando tokens semánticos
    const badgeVariants = {
      default: 'bg-[var(--surface-hover)] text-[var(--text-secondary)]',
      selected: 'bg-[var(--primary)] text-[var(--text-inverse)]',
      outline: 'bg-[var(--primary)] text-[var(--text-inverse)]',
      success: 'bg-[var(--success)] text-[var(--text-inverse)]',
      warning: 'bg-[var(--warning)] text-[var(--text-inverse)]',
      error: 'bg-[var(--error)] text-[var(--text-inverse)]',
      info: 'bg-[var(--info)] text-[var(--text-inverse)]',
    };

    const badgeStyle = badgeVariants[effectiveVariant as keyof typeof badgeVariants] || badgeVariants.default;

    const handleEndIconClick = (e: React.MouseEvent) => {
      if (onEndIconClick) {
        e.stopPropagation(); // Evita que se dispare el onClick del chip
        onEndIconClick(e);
      }
    };

    // Clone icons to apply proper sizing
    const renderIcon = (iconElement: React.ReactNode) => {
      if (!iconElement) return null;
      if (React.isValidElement(iconElement)) {
        return React.cloneElement(iconElement as React.ReactElement<{ className?: string }>, {
          className: cn(iconSize, (iconElement.props as { className?: string }).className),
        });
      }
      return iconElement;
    };

    return (
      <button
        ref={ref}
        type={type}
        className={cn(chipVariants({ variant: effectiveVariant, size }), className)}
        {...props}
      >
        {icon && (
          <span className="flex-shrink-0">
            {renderIcon(icon)}
          </span>
        )}

        {children && <span>{children}</span>}

        {badge !== undefined && (
          <span
            className={cn(
              'inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-xs font-semibold',
              badgeStyle
            )}
          >
            {badge}
          </span>
        )}

        {endIcon && (
          <span 
            className="flex-shrink-0 cursor-pointer"
            onClick={handleEndIconClick}
          >
            {renderIcon(endIcon)}
          </span>
        )}
      </button>
    );
  }
);

Chip.displayName = 'Chip';

export { Chip, chipVariants };


